import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';

import API, { Types } from '@/modules/API';
import Utils from '@/modules/Utils';
import PropUtils from '@/modules/PropUtils';
import vuex from '..';
import get from 'lodash/get';
import { getMapIcon, buildCabinetByIdMap } from '@/pages/mapView/components/map/getMapIcon';

const computeIconKey = (device, cabinetById?) => {
  try {
    device.__iconKey = getMapIcon(device, false, cabinetById);
  } catch (e) {
    // device may be missing fields during early commits; leave previous value if any
  }
};

export const base_columns = [
  'class_name',
  'dmodified_ts',
  'id',
  'meta.category',
  'meta.commission',
  'meta.device',
  'meta.location',
  'name',
  'power',
  'sys___active',
  'sys___active__modified',
  'sys___connected',
  'sys___online'
];

export const fixture_columns = [
  'parent_id', // lora - tondo
  'device.hardwareid',
  'device.schedule',
  'meta.imsi.imsi',
  'meta.type.type', // asset - tondo
  'meta.part_number.part_number',
  'meta.install_location',
  'meta.schedule_curve',
  'disconnect_reason',
  'version_nrf52840'
  // Dead/unused fields removed from the fixture streaming query to reduce payload
  // size (this stream is ~75MB for 28k devices). All were verified by usage search
  // to be either dead code or consumed only by other streams/views:
  //   'smodified_ts'     — never read
  //   'rssi'             — only used by cabinet AdvancedTab (not fixtures)
  //   'low_power_alert'  — normalized in GMap.setExtraFixtureData, never consumed
  //   'version_nrf52840' — declared on type, no consumer
  // 'meta.stream', // video
];

const cabinet_columns = [
  'created_ts',
  'device.hardwareid',
  'device.schedule',
  'device.version.os_release',
  'meta.imsi.imsi',
  'meta.hardware_ids.eth0',
  'meta.devices_schedule',
  'services.config',
  'dashboard.connection_display',
  'rssi',
  'circuits',
  'device_connected_percent'
];

const sensors_columns = [
  'water_mark',
  'water_height_trigger',
  'lsi1',
  'lsi2'
];

const STREAM_ITEMS_LIMIT = 999999999;
const STREAM_BATCH_SIZE = 1000;
const FIELDS_AFFECTING_ICON = new Set([
  'sys___active',
  'sys___connected',
  'power',
  'disconnect_reason',
  'meta.device',
  'meta.category',
  'meta.schedule_curve',
  'device.schedule',
  'services.config',
  'meta.devices_schedule'
]);
// Cabinet-side fields whose changes invalidate the icons of all fixtures attached
// to that cabinet. Other cabinet fields (e.g. sys___active, sys___connected, power)
// only affect the cabinet's own icon and must NOT trigger a fixture cascade.
const CABINET_FIELDS_AFFECTING_FIXTURES = new Set([
  'meta.device',
  'services.config',
  'meta.devices_schedule'
]);
export const REPORTING_QUERY_VERSION = 2;

/**
 * Cabinet Details on-demand fetch TTL. When the global stream completed within
 * this window AND the row already has all `CROSS_PAGE_CABINET_FIELDS`, the
 * page mounts without re-fetching the header (it relies on the store).
 */
export const CABINET_DETAILS_TTL_MS = 10 * 60 * 1000;

/**
 * Cabinet fields read by pages OTHER THAN Cabinet Details
 * (MapView, GMap, CabinetManagement, InventoryReport, AlertManagement,
 * ConfigCabinets, Reports.vue, Sidebar, etc.). Saves of these fields on the
 * Cabinet Details page are mirrored back to the store via `setDeviceField`.
 * Tab-only fields (cabinet.operation_state, electricity.price, live_data,
 * digital_input, control, device.connections, device.schedule, ...) are NOT
 * mirrored — they live only in component state for the open page.
 */
export const CROSS_PAGE_CABINET_FIELDS = [
  'id',
  'class_name',
  'meta.category',
  'meta.commission',
  'meta.device',
  'sys___active',
  'sys___connected',
  'services.config',
  'meta.devices_schedule'
];

const mqtt_map_streams = ['power', 'sys___active', 'sys___connected', 'lsi1', 'lsi2', 'water_mark', 'water_height_trigger'];
// const cabinets_class_names = `'Power_Meter_HummingBoard', 'Power_Meter_Hummingboard', 'sbc_cabinet', 'sbc_cabinet_extensions', 'Extension_Card', 'sbc_cabinet_pm_135', 'virtual_cabinet'`;
// const sensors_class_names = `'NB_BLE_nRF52V2_flood_sensor', 'Sensor', 'Water_Meter', 'rule_board'`;

export const setDeviceCommonFields = (device, default_location) => {
  try {
    device['meta.device'] = device['meta.device'] || {};
    device['meta.commission'] = PropUtils.parseMetaCommissionProperty(device['meta.commission'] || {});
    [device['meta.location'], device.has_valid_location, device.has_default_location] = PropUtils.parseMetaLocationProperty(device['meta.location'] || {}, default_location);
    device['meta.category'] = device['meta.category'] || { category: ''};
  } catch (e) {
    console.log('%c' + 'error:' + e + '\ndevice:' + device.id, 'background: #F74242; color: white');
  }
};

export const setCabinetFields = (device) => {
  try {
    device['meta.hardware_ids.eth0'] = device['meta.hardware_ids.eth0'] || '';
    device['meta.devices_schedule'] = device['meta.devices_schedule'] || {};
    device['meta.imsi.imsi'] = device['meta.imsi.imsi'] || '';
    device['device.version.os_release'] = device['device.version.os_release'] || '';
    device['device.hardwareid'] = device['device.hardwareid'] || { id: '' };
    device['device.schedule'] = device['device.schedule'] || (Utils.hasCabinetClassWithNewFormat(device['device.version.os_release']) ? { schedules: {}, days_schedules: {}} : { schedule: [] });
    device['services.config'] = device['services.config'] || {};
    device['dashboard.connection_display'] = device['dashboard.connection_display'] || {
      enable_outputs: 1,
      enable_inputs: 1,
      display_outputs: 1,
      display_inputs: 1
    };

  } catch (e) {
    console.log('%c' + 'error:' + e + '\ndevice:' + device.id, 'background: #F74242; color: white');
  }
};

const setFixtureFields = (device) => {
  try {
    device['meta.imsi.imsi'] = device['meta.imsi.imsi'] || '';
    device['meta.type.type'] = device['meta.type.type'] || '';
    device['meta.part_number.part_number'] = device['meta.part_number.part_number'] || '';
    device['meta.install_location'] = device['meta.install_location'] || {
      gps_location: { lat: '', lng: '' },
      app_location: { lat: '', lng: '' },
      gis_location: { lat: '', lng: '' },
      manual_location: { lat: '', lng: '' }
    };
    device['meta.schedule_curve'] = device['meta.schedule_curve'] || {};
    device['device.hardwareid'] = device['device.hardwareid'] || { id: '' };
    device['device.schedule'] = device['device.schedule'] || { schedule: [] };
  } catch (e) {
    console.log('%c' + 'error:' + e + '\ndevice:' + device.id, 'background: #F74242; color: white');
  }
};

const setRestFields = (device) => {
  try{
    device['device.schedule'] = device['device.schedule'] || (Utils.hasCabinetClassWithNewFormat(device['device.version.os_release']) ? { schedules: {}, days_schedules: {}} : { schedule: [] });
  } catch (e) {
    console.log('%c' + 'error:' + e + '\ndevice:' + device.id, 'background: #F74242; color: white');
  }
};

@Module({ namespaced: true })
export default class Reports extends VuexModule {
  reportsList: any[] = [];
  devicesCount: number = -1;
  cabinetsCount: number = -1;
  pagesCount: number = 0;
  companyId: string = '';
  projectId: string = '';
  allProjectsData: any = {};
  devicesChangedFromMqtt = new Map();
  // True once ALL streaming endpoints (cabinets/fixtures/rest) have completed.
  // This is a definitive signal — unlike `devicesCount === reportsList.length`,
  // which can be transiently true when devicesCount is updated mid-stream.
  streamingComplete: boolean = false;
  // Wall-clock timestamp of the last `setStreamingComplete(true)` flip. Anchors
  // the Cabinet Details TTL so on-demand consumers can decide whether the
  // store is fresh enough to serve them without re-fetching.
  streamingCompleteAt: number | null = null;

  @Action
  async updateDeviceMode(project_id, device_id, mode) {
    return API.post(
      '',
      `${Types.PROJECTS}/${project_id}/${Types.DEVICES}/${device_id}/streams/sys___active/evt/`,
      { content: mode },
      {},
      'v4'
    );
  }

  @Action
  resetReports() {
    vuex.commit('Reports/clearReports');
  }

  @Mutation
  setDeviceField(data) {
    const device = this.reportsList.find((device) => device.id === data.device_id);
    if (device && device[data.field] !== data.value) {
      device[data.field] = !isNaN(data.value) ? +data.value : data.value;
      if (FIELDS_AFFECTING_ICON.has(data.field)) {
        computeIconKey(device);
        if (Utils.hasCabinetClass(device?.['meta.category']?.category) && CABINET_FIELDS_AFFECTING_FIXTURES.has(data.field)) {
          vuex.commit('Reports/recomputeFixtureIconsForCabinet', device);
        }
      }
      if (mqtt_map_streams.includes(data.field)){
        vuex.commit('Reports/setDeviceChangedFromMqtt', device);
      }
    }
  }

  @Mutation
  recomputeIconForDevice(device_id) {
    // Recomputes ONLY this device's own icon. Does NOT cascade to fixtures —
    // callers must invoke `recomputeFixtureIconsForCabinet` explicitly when fixture-affecting
    // cabinet fields change (services.config, meta.devices_schedule, meta.device.cabinet_id).
    const device = this.reportsList.find((d) => d.id === device_id);
    if (!device) return;
    computeIconKey(device);
    vuex.commit('Reports/setDeviceChangedFromMqtt', device);
  }

  @Mutation
  recomputeFixtureIconsForCabinet(cabinet) {
    const cabinetId = cabinet && cabinet['meta.device'] && cabinet['meta.device'].cabinet_id;
    if (!cabinetId) return;
    const cabinetById = buildCabinetByIdMap();
    this.reportsList.forEach((device) => {
      try {
        if (Utils.hasTondoClass(device['meta.category']?.category)) {
          const fixtureCabinetId = device['meta.device']?.cabinet_id || 'default';
          if (fixtureCabinetId === cabinetId) {
            computeIconKey(device, cabinetById);
            vuex.commit('Reports/setDeviceChangedFromMqtt', device);
          }
        }
      } catch (e) { /* skip */ }
    });
  }

  @Action
  recomputeAllIcons() {
    vuex.commit('Reports/_recomputeAllIcons');
  }

  // Post-stream pass that builds `Props.{cabinet,fixture}_meta_device_options`
  // in a single sweep over `reportsList`. Replaces the per-batch accumulation
  // that used to run inside `setDevicesReportsList` (quadratic as the existing
  // option list grew). Consumers of these options are non-map screens
  // (Inventory / Reports / Filters / CabinetManagement / device data tabs),
  // so deferring until after the map finishes streaming has no UX impact.
  @Action
  buildMetaDeviceOptions() {
    const cabinet_options = new Map<string, any[]>();
    const fixture_options = new Map<string, any[]>();

    const collect = (device, target: Map<string, any[]>) => {
      const meta_device = device['meta.device'];
      if (!meta_device) return;
      for (const field_name of Object.keys(meta_device)) {
        if (field_name.includes('backup')) continue;
        const value = meta_device[field_name];
        const bucket = target.get(field_name);
        if (bucket) bucket.push(value);
        else target.set(field_name, [value]);
      }
    };

    const list = (this.state as any).reportsList as any[];
    for (const device of list) {
      const category = device['meta.category']?.category;
      if (Utils.hasCabinetClass(category)) {
        if (device['meta.commission']?.commissioned) collect(device, cabinet_options);
      } else if (Utils.hasTondoClass(category)) {
        collect(device, fixture_options);
      }
    }

    if (cabinet_options.size) vuex.commit('Props/setCabinetMetaDeviceOptions', cabinet_options);
    if (fixture_options.size) vuex.commit('Props/setFixtureMetaDeviceOptions', fixture_options);
  }

  @Mutation
  _recomputeAllIcons() {
    // Single post-stream pass that populates __iconKey for every device. Runs once
    // after all three streams (cabinets/fixtures/rest) have completed, so the cabinet
    // lookup is fully populated and fixture icons resolve their parent correctly.
    const cabinetById = buildCabinetByIdMap();
    this.reportsList.forEach((device) => {
      try {
        computeIconKey(device, cabinetById);
      } catch (e) { /* skip */ }
    });
  }

  @Mutation
  setDeviceChangedFromMqtt(device) {
      this.devicesChangedFromMqtt = new Map(this.devicesChangedFromMqtt).set(device.id, device);

  }

  @Mutation
  removeDeviceChangedFromMqtt(device_id) {
    this.devicesChangedFromMqtt.delete(device_id);
  }

  @Mutation
  clearReports() {
    this.reportsList = [];
  }

  @Mutation
  setStreamingComplete(value: boolean) {
    this.streamingComplete = value;
    if (value) {
      this.streamingCompleteAt = Date.now();
    }
  }

  @Mutation
  setDevicesReportsList(data: any) {
    // NOTE: previously this mutation also built `meta_device_options` per batch
    // and committed `Props/{setCabinetMetaDeviceOptions,setFixtureMetaDeviceOptions}`
    // for each batch. That ran an `Object.entries(meta.device)` per device PLUS a
    // sort + Set-based dedupe per field per batch (quadratic as the existing
    // option list grew). For 28k+ devices this dominated streaming CPU.
    //
    // The meta-device options are only consumed by report/inventory/filter/
    // cabinet-management UIs (never by the map), so they are now built once
    // after all streaming completes — see `Reports/buildMetaDeviceOptions`,
    // dispatched from `getAllDevices` / `getAllDevicesAllProjects`.
    if (data.type === 'cabinet') {
      data.devices.forEach((device) => {
        setDeviceCommonFields(device, data.default_location);
        setCabinetFields(device);
        // __iconKey is intentionally NOT pre-computed here to keep the streaming
        // hot-path light. A single post-stream pass populates it for all devices
        // (see getAllDevices → recomputeAllIcons).
      });
    } else if (data.type === 'fixture') {
      data.devices.forEach((device) => {
        setDeviceCommonFields(device, data.default_location);
        setFixtureFields(device);
      });
    } else {
      // rest
      data.devices.forEach((device) => {
        setDeviceCommonFields(device, data.default_location);
        setRestFields(device);
      });
    }

    vuex.commit('Reports/setDevicesReports', data.devices);
  }

  @Mutation
  setDevicesReports(data: any) {
    // Dedupe-and-merge by id. Without this, when CabinetDetails (or any other
    // on-demand consumer) calls `upsertDeviceColumns` to insert a stub row
    // before the global stream finishes, the global stream would later push a
    // second row for the same id. The find() lookup is O(N) per incoming row,
    // but the existing-row case only triggers for the (small) number of rows
    // CabinetDetails has touched; the common case (no pre-existing row) hits
    // the fast path.
    if (!this.reportsList.length) {
      this.reportsList.push(...data);
      return;
    }
    const byId = new Map<string, any>();
    this.reportsList.forEach((row) => byId.set(row.id, row));
    data.forEach((incoming) => {
      const existing = byId.get(incoming.id);
      if (!existing) {
        this.reportsList.push(incoming);
        return;
      }
      // Merge incoming columns onto the existing row — incoming wins because
      // the global stream is the most complete snapshot at this moment.
      let iconAffected = false;
      Object.keys(incoming).forEach((field) => {
        if (existing[field] !== incoming[field]) {
          existing[field] = incoming[field];
          if (FIELDS_AFFECTING_ICON.has(field)) iconAffected = true;
        }
      });
      if (iconAffected) computeIconKey(existing);
    });
  }

  @Mutation
  setDevicesCount(count) {
    this.devicesCount = this.devicesCount === -1 ? count : this.devicesCount + count;
  }

  @Mutation
  setCabinetsCount() {
    this.cabinetsCount = vuex.getters['Reports/commissionedCabinets'].length;
  }

  @Mutation
  clearCabinetsCount() {
    this.cabinetsCount = -1;
  }

  @Mutation
  clearDevicesCount(){
    this.devicesCount = -1;
  }

  @Mutation
  setPagesCount(count) {
    this.pagesCount = count;
  }

  @Mutation
  setCompanyId(id) {
    this.companyId = id;
  }

  @Mutation
  setProjectId(id) {
    this.projectId = id;
  }

  @Mutation
  setAllProjectsData(allProjectsData) {
    this.allProjectsData = allProjectsData;
  }

  @Mutation
  deleteDeviceFromList(deviceId) {
    this.reportsList = this.reportsList.filter((device) => device.id !== deviceId);
    vuex.commit('Reports/setDevicesCount', -1);
  }

  @Mutation
  addDeviceToList(device) {
    computeIconKey(device);
    // Dedupe — if a stub row was upserted by Cabinet Details earlier, merge
    // into it instead of duplicating.
    const existing = this.reportsList.find((row) => row.id === device.id);
    if (existing) {
      Object.keys(device).forEach((field) => {
        existing[field] = device[field];
      });
      return;
    }
    this.reportsList.push(device);
    vuex.commit('Reports/setDevicesCount', 1);
  }

  /**
   * Multi-field idempotent merge for an on-demand reporting-query result.
   * - If a row with the given id already exists → merge the listed `columns`
   *   onto it (only fields actually present on `device` are merged).
   * - Else → push a new row with just those fields.
   * - Recomputes `__iconKey` if any iconified field changed.
   *
   * Used by Cabinet Details to mirror the cross-page subset back to the
   * store after its on-demand fetches and saves.
   */
  @Mutation
  upsertDeviceColumns(payload: { device: any; columns: string[] }) {
    const { device, columns } = payload;
    if (!device || !device.id || !columns || !columns.length) return;
    const existing = this.reportsList.find((row) => row.id === device.id);
    let iconAffected = false;
    if (existing) {
      columns.forEach((field) => {
        if (!(field in device)) return;
        if (existing[field] !== device[field]) {
          existing[field] = device[field];
          if (FIELDS_AFFECTING_ICON.has(field)) iconAffected = true;
        }
      });
      if (iconAffected) {
        computeIconKey(existing);
        if (
          Utils.hasCabinetClass(existing?.['meta.category']?.category) &&
          columns.some((f) => CABINET_FIELDS_AFFECTING_FIXTURES.has(f))
        ) {
          vuex.commit('Reports/recomputeFixtureIconsForCabinet', existing);
        }
      }
    } else {
      const newRow: any = { id: device.id };
      columns.forEach((field) => {
        if (field in device) newRow[field] = device[field];
      });
      computeIconKey(newRow);
      this.reportsList.push(newRow);
    }
  }

  @Mutation
  editDeviceField(field_data) {
    const device_to_update = this.reportsList.find((device) => device.id === field_data.device_id);
    if (device_to_update) {
      let topLevelField: string;
      if (Array.isArray(field_data.name)) {
        Utils.setNestedValue(device_to_update, field_data.name, field_data.value);
        topLevelField = field_data.name[0];
      } else {
        device_to_update[field_data.name] = field_data.value;
        topLevelField = field_data.name;
      }
      if (FIELDS_AFFECTING_ICON.has(topLevelField)) {
        computeIconKey(device_to_update);
        if (Utils.hasCabinetClass(device_to_update?.['meta.category']?.category) && CABINET_FIELDS_AFFECTING_FIXTURES.has(topLevelField)) {
          vuex.commit('Reports/recomputeFixtureIconsForCabinet', device_to_update);
        }
        vuex.commit('Reports/setDeviceChangedFromMqtt', device_to_update);
      }
    }
  }

  @Action
  async loadReports(data) {
    const user = localStorage.getItem('user') && JSON.parse(localStorage.getItem('user'));

    vuex.commit('Props/defineProjectDefaultLocation', data.id);
    vuex.commit('Global/setProjectSunTime');
    vuex.commit('Reports/setCompanyId', data.company || (user && user.company));
    vuex.commit('Reports/setProjectId', data.id);

    if (data.id === 'allProjects') {
      vuex.commit('Reports/setAllProjectsData', data);
    }

    try {
      if (data.id === 'allProjects') {
        // Fire the count and the streams in parallel so the count's network
        // round-trip overlaps with the streams' TTFB instead of being a serial
        // prefix. The count populates `devicesCount` (loader denominator) as
        // soon as it lands; until then the loader stays in the indeterminate
        // "Preparing…" state, which is the same UX as before.
        const count$ = vuex.dispatch('Reports/getPagesCountAllProjects', data);
        const stream$ = vuex.dispatch('Reports/getAllDevicesAllProjects', data);
        await Promise.all([count$, stream$]);
      } else {
        // Same parallelization for the single-project flow. `getDevicesCount`
        // is the small `count(*)` query; the 3 device streams (cabinets /
        // fixtures / rest) are the heavy ones — there is no ordering
        // dependency between them.
        const count$ = vuex.dispatch('Reports/getDevicesCount');
        const stream$ = vuex.dispatch('Reports/getAllDevices');
        await Promise.all([count$, stream$]);
      }
    } catch (e) {
      vuex.commit('Errors/setError', get(e, 'response.data.error.message', e));
      vuex.commit('Errors/setShowError', true);
      console.error(e);
    }
  }


  @Action
  async getDevicesCount() {
    // Count EVERY device that will end up in `reportsList` once streaming
    // completes — commissioned devices of any category PLUS uncommissioned
    // cabinets (HUBs) — so `devicesCount` is final before the first batch
    // arrives. Previously the count covered commissioned only and the
    // uncommissioned-cabinet adjustment was committed mid-stream from
    // `fetchCabinets`, which caused the loader percentage to flicker
    // (jump high, then drop when the denominator grew).
    const request_data = {
      type: 'count',
      columns: ['count(*)'],
      where: [
        "(meta.commission.commissioned=true OR meta.category.category='HUB')"
      ],
      version: REPORTING_QUERY_VERSION
    };
    try {
      await vuex.dispatch('Reports/loadReportsPage', request_data);
    } catch (e) {
      throw e;
    }
  }

  @Action
  async getAllDevices() {
    try {
      vuex.commit('Reports/setStreamingComplete', false);
      // Run in parallel for fastest streaming. After all complete, run a one-time
      // recompute to fix any fixture icons that were committed before their cabinet arrived.
      const cabinets$ = vuex.dispatch('Reports/fetchCabinets');
      const fixtures$ = vuex.dispatch('Reports/fetchFixtures');
      const rest$ = vuex.dispatch('Reports/fetchRest');
      Promise.all([cabinets$, fixtures$, rest$]).then(async () => {
        // Yield between each heavy post-stream pass so the browser can paint
        // the loader's 'Rendering map…' label and the map component can start
        // its own chunked feature creation, instead of the sync passes blocking
        // the main thread back-to-back. `buildMetaDeviceOptions` is deferred to
        // the next idle window (see below) so it never delays the moment when
        // markers actually appear on screen.
        const yieldFrame = () => new Promise((r) => requestAnimationFrame(() => r(undefined)));
        await yieldFrame();
        vuex.dispatch('Reports/recomputeAllIcons');
        await yieldFrame();
        vuex.commit('Reports/setStreamingComplete', true);
        // Defer `buildMetaDeviceOptions` to idle. The map's chunked marker
        // render runs many rAFs after `setStreamingComplete` flips; requestIdle
        // only fires once the main thread quiets down, so the options are
        // computed after all icons are painted. Falls back to setTimeout for
        // browsers without requestIdleCallback (e.g. Safari).
        const scheduleIdle = (cb: () => void) => {
          if (typeof (window as any).requestIdleCallback === 'function') {
            (window as any).requestIdleCallback(cb, { timeout: 5000 });
          } else {
            setTimeout(cb, 500);
          }
        };
        scheduleIdle(() => vuex.dispatch('Reports/buildMetaDeviceOptions'));
      });
    } catch (e) {
      throw e;
    }
  }

  @Action
  fetchRest(){
    vuex.dispatch('Reports/loadReportsPage', {
      type: 'rest',
      columns: base_columns.concat(sensors_columns),
      default_location: (vuex.state as any).Props.project_default_location,
      where: [
        "meta.category.category!='HUB'",
        "meta.category.category!='FIXTURE'",
        "meta.commission.commissioned=true"
      ],
      version: REPORTING_QUERY_VERSION
    });
  }

  @Action
  async fetchCabinets(){
    await vuex.dispatch('Reports/loadReportsPage', {
      type: 'cabinet',
      columns: base_columns.concat(cabinet_columns),
      default_location: (vuex.state as any).Props.project_default_location,
      where: ["meta.category.category='HUB'"],
      version: REPORTING_QUERY_VERSION
    });
    vuex.commit('Reports/setCabinetsCount');
    // NOTE: uncommissioned cabinets are already included in the initial
    // `devicesCount` (see `getDevicesCount`), so no mid-stream adjustment
    // is needed here — adjusting at this point used to make the loader
    // percentage drop visibly when cabinets finished streaming.
  }

  @Action
  async fetchFixtures(){
    await vuex.dispatch('Reports/loadReportsPage', {
      type: 'fixture',
      columns: base_columns.concat(fixture_columns),
      default_location: (vuex.state as any).Props.project_default_location,
      where: ["meta.category.category='FIXTURE'", "meta.commission.commissioned=true"],
      version: REPORTING_QUERY_VERSION
    });
  }

  @Action
  async loadReportsPage(request_data) {
    const project_id = (this.state as any).projectId;
    const company_id = (this.state as any).companyId;
    if (project_id === 'allProjects') return;

    const isCount = request_data.type === 'count';

    const body: any = {
      columns: request_data.columns,
      items: STREAM_ITEMS_LIMIT,
      version: REPORTING_QUERY_VERSION
    };

    if (request_data.order_by) body.order_by = request_data.order_by;
    if (request_data.where) body.where = request_data.where;

    const apiParams: any = {};
    if (!isCount) {
      apiParams.batchSize = STREAM_BATCH_SIZE;
      apiParams.onBatch = (batch: any[]) => {
        vuex.commit('Reports/setDevicesReportsList', {
          type: request_data.type,
          devices: batch,
          default_location: request_data.default_location
        });
      };
    }

    try {
      const result = await API.streamingPost(
        Types.COMPANIES,
        `/${company_id}/projects/${project_id}/streaming_reporting_query/`,
        body,
        apiParams
      );
      if (isCount && result && result.length) {
        vuex.commit('Reports/setDevicesCount', result[0].count);
      }
    } catch (e) {
      throw e;
    }
  }

  
  @Action
  async getPagesCountAllProjects(allProjectsData) {
    const request_data = {
      type: 'count',
      columns: ['count(*)'],
      where: [
        "meta.commission.commissioned=true"
      ],
      version: REPORTING_QUERY_VERSION
    };
    try {
      await vuex.dispatch('Reports/loadReportsPageAllProjects', {
        request_data,
        allProjectsData
      });
    } catch (e) {
      throw e;
    }
  }

  @Action
  async getAllDevicesAllProjects(data) {
    try {
      vuex.commit('Reports/setStreamingComplete', false);
      const cabinets$ = vuex.dispatch('Reports/fetchCabinetsAllProjects', data);
      const fixtures$ = vuex.dispatch('Reports/fetchFixturesAllProjects', data);
      const rest$ = vuex.dispatch('Reports/fetchRestAllProjects', data);
      Promise.all([cabinets$, fixtures$, rest$]).then(async () => {
        // See `getAllDevices` for rationale; same yield + idle-defer pattern.
        const yieldFrame = () => new Promise((r) => requestAnimationFrame(() => r(undefined)));
        await yieldFrame();
        vuex.dispatch('Reports/recomputeAllIcons');
        await yieldFrame();
        vuex.commit('Reports/setStreamingComplete', true);
        const scheduleIdle = (cb: () => void) => {
          if (typeof (window as any).requestIdleCallback === 'function') {
            (window as any).requestIdleCallback(cb, { timeout: 5000 });
          } else {
            setTimeout(cb, 500);
          }
        };
        scheduleIdle(() => vuex.dispatch('Reports/buildMetaDeviceOptions'));
      });
    } catch (e) {
      throw e;
    }
  }

  @Action
  async fetchCabinetsAllProjects(data){
    const request_data = {
      type: 'cabinet',
      columns: base_columns.concat(cabinet_columns),
      default_location: (vuex.state as any).Props.project_default_location,
      order_by: 'id',
      where: ["meta.category.category='HUB'"],
      version: REPORTING_QUERY_VERSION
    };
    await vuex.dispatch('Reports/loadReportsPageAllProjects', {
      request_data,
      allProjectsData: data
    });
    vuex.commit('Reports/setCabinetsCount');
    // See `fetchCabinets` for why no mid-stream `setDevicesCount` adjustment
    // is needed: uncommissioned cabinets are already part of the initial count.
  }

  @Action
  fetchRestAllProjects(data){
    const request_data = {
      type: 'rest',
      columns: base_columns.concat(sensors_columns),
      default_location: (vuex.state as any).Props.project_default_location,
      where: [
        "meta.category.category!='HUB'",
        "meta.category.category!='FIXTURE'",
        "meta.commission.commissioned=true"
      ],
      version: REPORTING_QUERY_VERSION
    };
    vuex.dispatch('Reports/loadReportsPageAllProjects', {
      request_data,
      allProjectsData: data
    });
  }

  @Action
  async fetchFixturesAllProjects(data){
    const request_data = {
      type: 'fixture',
      columns: base_columns.concat(fixture_columns),
      default_location: (vuex.state as any).Props.project_default_location,
      where: ["meta.category.category='FIXTURE'", "meta.commission.commissioned=true"],
      version: REPORTING_QUERY_VERSION
    };
    await vuex.dispatch('Reports/loadReportsPageAllProjects', {
      request_data,
      allProjectsData: data
    });
  }

  @Action
  async loadReportsPageAllProjects({ request_data, allProjectsData }) {
    if (!allProjectsData || allProjectsData === undefined || !allProjectsData.projects) return;

    const isCount = request_data.type === 'count';

    for await (const item of allProjectsData.projects) {
      const project_id = item.id;
      const company_id = item.company;

      const body: any = {
        columns: request_data.columns,
        items: STREAM_ITEMS_LIMIT,
        version: REPORTING_QUERY_VERSION
      };

      if (request_data.order_by) body.order_by = request_data.order_by;
      if (request_data.where) body.where = request_data.where;

      const apiParams: any = {};
      if (!isCount) {
        apiParams.batchSize = STREAM_BATCH_SIZE;
        apiParams.onBatch = (batch: any[]) => {
          vuex.commit('Reports/setDevicesReportsList', {
            type: request_data.type,
            devices: batch,
            default_location: request_data.default_location
          });
        };
      }

      try {
        const result = await API.streamingPost(
          Types.COMPANIES,
          company_id + '/projects/' + project_id + '/streaming_reporting_query/',
          body,
          apiParams
        );
        if (isCount && result && result.length && result[0].count) {
          vuex.commit('Reports/setDevicesCount', result[0].count);
        }
      } catch (e) {
        console.log(`error: ${JSON.stringify(e)}`);
        throw e;
      }
    }
  }

  get devicesList_map() {
    const devices_map = new Map();
    this.reportsList.forEach((device) => devices_map.set(device.id, device));
    return devices_map;
  }

  get cabinets() {
    return this.reportsList.filter((device) => Utils.hasCabinetClass(device['meta.category']?.category));
  }

  get commissionedFixtures() {
    return this.reportsList.filter((device) => Utils.hasTondoClass(device['meta.category']?.category));
  }

  get commissionedFixtures_map() {
    const devices_map = new Map();
    vuex.getters['Reports/commissionedFixtures'].forEach((device) => {
      devices_map.set(device.id, device);
    });
    return devices_map;
  }

  get cabinets_fixtures() {
    // Two-pass O(C + F) instead of O(C × F). On a ~600-cabinet / ~28k-fixture
    // project the previous nested filter cost ~16.8M comparisons every time
    // this getter recomputed (e.g. on cabinet-details page mount). The pass
    // below is a single linear scan of cabinets followed by a single linear
    // scan of fixtures with constant-time bucket lookup.
    const devices_by_cabinet = new Map();
    const cabinet_id_to_device_id = new Map();
    vuex.getters['Reports/commissionedCabinets'].forEach((cabinet) => {
      const cabinet_id = cabinet['meta.device'] && cabinet['meta.device'].cabinet_id;
      if (!cabinet_id) return;
      cabinet_id_to_device_id.set(cabinet_id, cabinet.id);
      devices_by_cabinet.set(cabinet.id, []);
    });
    vuex.getters['Reports/commissionedFixtures'].forEach((fixture) => {
      const cabinet_id = fixture['meta.device'] && fixture['meta.device'].cabinet_id;
      const cabinet_device_id = cabinet_id_to_device_id.get(cabinet_id);
      if (!cabinet_device_id) return;
      devices_by_cabinet.get(cabinet_device_id).push(fixture);
    });
    return devices_by_cabinet;
  }

  get ruleBoards() {
    return this.reportsList.filter((device) => Utils.hasRuleBoardClass(device.class_name));
  }

  get commissionedDevices() {
    return this.reportsList.filter((device) => device['meta.commission']?.commissioned);
  }

  get commissioned_map() {
    const devices_map = new Map();
    vuex.getters['Reports/commissionedDevices'].forEach((device) => {
      devices_map.set(device.id, device);
    });
    return devices_map;
  }

  get commissionedWithLocation() {
    return this.reportsList.filter(
      (device) => 
        device['meta.commission']?.commissioned &&
        device.has_valid_location
    );
  }

  get commissionedCabinets() {
    return vuex.getters['Reports/cabinets'].filter(
      (device) => device['meta.commission']?.commissioned
    );
  }

  get commissionedCabinets_map() {
    const devices_map = new Map();
    vuex.getters['Reports/commissionedCabinets'].forEach((device) => {
      devices_map.set(device.id, device);
    });
    return devices_map;
  }

  get commissionedMotionSensor() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasMotionSensorClass(device.class_name)
    );
  }

  get commissionedFloodSensor() {
    return this.reportsList.filter(
      (device) => Utils.hasFloodSensorClass(device['meta.category']?.category)
    );
  }

  get commissionedBarrier(){
    return this.reportsList.filter(
      (device) =>
        Utils.hasBarrierClass(device['meta.category']?.category)
    );
  }

  get commissionedWaterMeter() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasWaterMeterClass(device.class_name)
    );
  }

  get commissionedWeatherStation() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasWeatherStationClass(device.class_name)
    );
  }

  get commissionedRadar() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasRadarClass(device.class_name)
    );
  }

  /** Lookup helper for Cabinet Details (reactive). */
  get cabinet_by_id() {
    return (id: string) => this.reportsList.find((row) => row.id === id) || null;
  }

  /**
   * Returns true if the cabinet row exists in the store with all
   * `CROSS_PAGE_CABINET_FIELDS` AND was filled by the global stream
   * within `CABINET_DETAILS_TTL_MS`. Used by CabinetDetails on mount to
   * decide whether to skip the full header reporting query.
   */
  get cabinetCrossPageFresh() {
    const completedAt = this.streamingCompleteAt;
    return (id: string) => {
      if (!completedAt) return false;
      if (Date.now() - completedAt > CABINET_DETAILS_TTL_MS) return false;
      const row = this.reportsList.find((r) => r.id === id);
      if (!row) return false;
      return CROSS_PAGE_CABINET_FIELDS.every((field) => field in row);
    };
  }

  get commissionedCamera() {
    return this.reportsList.filter(
      (device) =>
        Utils.hasCameraClass(device['meta.category'].category)
    );
  }

  get motion_sensor_and_meta_device_map() {
    const devices_map = new Map();
    vuex.getters['Reports/commissionedMotionSensor'].forEach((device) => {
      const metaDevice = device['meta.device'];
      devices_map.set(device.id, [device, metaDevice]);
    });
    return devices_map;
  }

  get assetTypeList() {
    const assetTypeList = new Set();

    vuex.getters['Reports/commissionedWithLocation'].forEach((device) => {
      if (device['meta.type.type']) assetTypeList.add(device['meta.type.type']);
    });

    return [...assetTypeList];
  }

  get devicesTypeList() {
    const devicesTypeList = new Set();

    vuex.getters['Reports/commissionedWithLocation'].forEach((device) => {
      if (device['meta.category'].category) devicesTypeList.add(device['meta.category'].category);
    });

    return [...devicesTypeList];
  }

  get deviceParentList() {
    const parentList = this.reportsList.reduce((r, a) => {
      r[a.parent_id] = r[a.parent_id] || [];
      r[a.parent_id].push(a);
      return r;
    }, Object.create(null));
    return parentList;
  }
}
